export const SITE_NAME = '青途 AI'

export function formatDateTime(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

export function parseReportJson(value) {
  if (!value) return {}
  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch {
      return {}
    }
  }
  if (typeof value === 'object') return value
  return {}
}

export function safeItems(value) {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.items)) return value.items
  if (Array.isArray(value?.repositories)) return value.repositories
  if (Array.isArray(value?.reports)) return value.reports
  if (Array.isArray(value?.data?.items)) return value.data.items
  if (Array.isArray(value?.data?.repositories)) return value.data.repositories
  if (Array.isArray(value?.data?.reports)) return value.data.reports
  return []
}

export const getArrayItems = safeItems

export function asStringList(value) {
  return safeItems(value).filter((item) => typeof item === 'string' && item.trim())
}

export function firstText(value, fallback = '') {
  if (typeof value === 'string' && value.trim()) return value.trim()
  return fallback
}

export function buildAnalysisShareUrl(reportId) {
  const url = new URL(window.location.origin)
  url.hash = `/github-analysis/share/${encodeURIComponent(reportId)}`
  return url.href
}

export function extractProjectHealthSections(reportJson) {
  const report = parseReportJson(reportJson)
  const techStack = asStringList(
    report.techStack || report.technicalStack || report.stack,
  )
  const highlights = asStringList(report.technicalHighlights || report.highlights)
  const optimizationSuggestions = asStringList(
    report.optimizationSuggestions
      || report.improvementSuggestions
      || report.suggestions,
  )
  const interviewTalkingPoints = asStringList(
    report.interviewTalkingPoints
      || report.interviewExpression
      || report.resumeValue?.resumeAngles,
  )
  const suitableUsers = asStringList(
    report.suitableUsers || report.learningValue?.suitableFor,
  )
  const risks = asStringList(report.risks || report.riskItems || report.weaknesses)
  const learningPath = asStringList(
    report.learningPath30Days
      || report.learningPath
      || report.studyPlan
      || report.learningValue?.learningPath,
  )
  const nextActions = asStringList(
    report.nextActions
      || report.recommendedNextSteps
      || report.actionItems
      || report.actions,
  )

  return {
    report,
    summary: firstText(report.summary),
    techStack,
    highlights,
    optimizationSuggestions,
    interviewTalkingPoints,
    suitableUsers,
    risks,
    learningPath,
    nextActions,
    learningValue: {
      reason: firstText(report.learningValue?.reason),
      items: asStringList(report.learningValue?.suitableFor),
      score: report.learningValue?.score,
    },
    resumeAdvice: {
      reason: firstText(report.resumeValue?.reason),
      items: asStringList(report.resumeValue?.resumeAngles),
      score: report.resumeValue?.score,
    },
    improvement: {
      items: optimizationSuggestions,
      reason: firstText(report.improvementReason || report.optimizationReason),
    },
  }
}

export function buildAnalysisPromotionText(report, shareUrl) {
  const repoName = report?.repoFullName || '这个 GitHub 项目'
  return `我用这个网站分析了 ${repoName} 项目，它自动整理了技术栈、项目亮点、学习价值和简历表达，适合想找项目练手/写简历的同学。\n${shareUrl}`
}

export async function copyText(value) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value)
      return
    } catch {
      // Fall back for browsers that expose Clipboard API without write permission.
    }
  }

  const textarea = document.createElement('textarea')
  textarea.value = value
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.focus()
  textarea.select()
  textarea.setSelectionRange(0, textarea.value.length)
  const copied = document.execCommand('copy')
  textarea.remove()
  if (!copied) throw new Error('COPY_FAILED')
}
